import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { SoundButton, Tabs } from "@/shared/ui";
import PlayerMenuSettingsChatCommonContainer from "@/domains/playerMenu/containers/settings/chat/common/PlayerMenuSettingsChatCommonContainer";
import PlayerMenuSettingsChatMessageContainer from "@/domains/playerMenu/containers/settings/chat/message/PlayerMenuSettingsChatMessageContainer";
import PlayerMenuContent from "@/domains/playerMenu/components/PlayerMenuContent";
import PlayerMenuContentTitle from "@/domains/playerMenu/components/PlayerMenuContentTitle";
import useChatSettings from "@/domains/settings/chat/hooks/useChatSettings";
import {
  appliedChatSettingsSelector,
  defaultChatSettingsSelector,
  reset,
  setMemoizedSettings,
} from "@/domains/settings/chat/slice";

enum Tab {
  Common = "Common",
  Message = "Message",
}

export default function PlayerMenuSettingsChatContainer() {
  const { t } = useTranslation("playerMenu");
  const [isPending, setIsPending] = useState(false);
  const dispatch = useAppDispatch();
  const appliedChatSettings = useAppSelector(appliedChatSettingsSelector);
  const defaultChatSettings = useAppSelector(defaultChatSettingsSelector);
  const { updateChatSettings } = useChatSettings();
  const [currTab, setCurrTab] = useState<Tab>(Tab.Common);

  useEffect(() => {
    dispatch(setMemoizedSettings(appliedChatSettings));

    return () => {
      dispatch(reset());
    };
  }, [dispatch]);

  const resetToDefaultHandler = useCallback(async () => {
    setIsPending(true);
    await updateChatSettings(defaultChatSettings);
    setIsPending(false);
  }, [updateChatSettings, defaultChatSettings]);

  const saveHandler = useCallback(async () => {
    setIsPending(true);
    await updateChatSettings(appliedChatSettings);
    setIsPending(false);
  }, [appliedChatSettings, updateChatSettings]);

  const tabs = useMemo(
    () => [
      { value: Tab.Common, label: t(`playerMenu.tab.Chat${Tab.Common}`) },
      { value: Tab.Message, label: t(`playerMenu.tab.Chat${Tab.Message}`) },
    ],
    [],
  );

  const header = (
    <div>
      <PlayerMenuContentTitle>{t("playerMenu.tabTitle.Chat")}</PlayerMenuContentTitle>
      <Tabs tabs={tabs} value={currTab} onClick={(value) => setCurrTab(value as Tab)} />
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between">
      <SoundButton
        className="h-[2.75rem]"
        loading={isPending}
        disabled={isPending}
        onClick={saveHandler}
      >
        Сохранить изменения
      </SoundButton>
      <SoundButton
        variant="soft"
        color="gray"
        className="h-[2.75rem]"
        loading={isPending}
        disabled={isPending}
        onClick={resetToDefaultHandler}
      >
        Сбрость к стандартным
      </SoundButton>
    </div>
  );

  if (!appliedChatSettings) {
    return null;
  }

  return (
    <PlayerMenuContent header={header} footer={footer}>
      {currTab === Tab.Common && <PlayerMenuSettingsChatCommonContainer />}
      {currTab === Tab.Message && <PlayerMenuSettingsChatMessageContainer />}
    </PlayerMenuContent>
  );
}
